import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { GlobalPresenceProvider } from './contexts/GlobalPresenceContext';
import PrivateRoute from './components/PrivateRoute';
import AuthPage from './pages/AuthPage';
import Dashboard from './pages/Dashboard';
import EditorPage from './pages/EditorPage';
import MoodBoard from './components/MoodBoard';
import ProfilePage from './pages/ProfilePage';
import TemplatesPage from './pages/TemplatesPage';
import TeamDetailsPage from './pages/TeamDetailsPage';
import TeamsPage from './pages/TeamsPage';
import BrainstormingBoardPage from './pages/BrainstormingBoardPage';
import BrainstormingListPage from './pages/BrainstormingListPage';
import KanbanBoardPage from './pages/KanbanBoardPage';
import DesignSprintModule from './pages/DesignSprintModule';
import DesignSprintsListPage from './pages/DesignSprintsListPage';
import MoodBoardEditor from './pages/MoodBoardEditor';
import ModernMoodBoardEditor from './pages/ModernMoodBoardEditor';
import MoodBoardEditorV2 from './pages/MoodBoardEditorV2';
import MoodBoardEditorV3 from './pages/MoodBoardEditorV3';
import LandingPage from './pages/LandingPage';
import ContactPage from './pages/ContactPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsOfServicePage from './pages/TermsOfServicePage';
import HelpSupportPage from './pages/HelpSupportPage';
import StatusPage from './pages/StatusPage';
import AccessibilityPolicyPage from './pages/AccessibilityPolicyPage';
import BillingSuccess from './pages/BillingSuccess';
import BillingCancel from './pages/BillingCancel';

// Helper wrapper to pass boardId from params
const MoodBoardEditorV2Wrapper = () => {
  const { boardId } = useParams();
  return <MoodBoardEditorV2 boardId={boardId!} />;
};

const MoodBoardEditorV3Wrapper = () => {
  const { boardId } = useParams();
  return <MoodBoardEditorV3 boardId={boardId!} />;
};

const App: React.FC = () => {
  return (
    <Router>
      <AuthProvider>
        <GlobalPresenceProvider>
          <Routes>
            <Route path="/auth" element={<AuthPage />} />
            <Route
              path="/dashboard"
              element={
                <PrivateRoute>
                  <Dashboard />
                </PrivateRoute>
              }
            />
            <Route
              path="/profile"
              element={
                <PrivateRoute>
                  <ProfilePage />
                </PrivateRoute>
              }
            />
            <Route
              path="/moodboard"
              element={
                <PrivateRoute>
                  <MoodBoard />
                </PrivateRoute>
              }
            />
            <Route
              path="/editor/:templateId"
              element={
                <PrivateRoute>
                  <EditorPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/templates"
              element={
                <PrivateRoute>
                  <TemplatesPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/teams"
              element={
                <PrivateRoute>
                  <TeamsPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/teams/:teamId"
              element={
                <PrivateRoute>
                  <TeamDetailsPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/teams/:teamId/templates"
              element={
                <PrivateRoute>
                  <TemplatesPage />
                </PrivateRoute>
              }
            />
            {/* New brainstorming routes */}
            <Route
              path="/teams/:teamId/brainstorming"
              element={
                <PrivateRoute>
                  <BrainstormingListPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/teams/:teamId/brainstorming/:boardId"
              element={
                <PrivateRoute>
                  <BrainstormingBoardPage />
                </PrivateRoute>
              }
            />
            {/* Legacy route retained */}
            <Route
              path="/brainstorming-board/:teamId"
              element={
                <PrivateRoute>
                  <BrainstormingBoardPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/teams/:teamId/kanban"
              element={
                <PrivateRoute>
                  <KanbanBoardPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/teams/:teamId/design-sprints"
              element={
                <PrivateRoute>
                  <DesignSprintsListPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/teams/:teamId/design-sprints/:sprintId"
              element={
                <PrivateRoute>
                  <DesignSprintModule />
                </PrivateRoute>
              }
            />
            <Route
              path="/design-sprint/:sprintId"
              element={
                <PrivateRoute>
                  <DesignSprintModule />
                </PrivateRoute>
              }
            />
            <Route
              path="/teams/:teamId/moodboards/:moodboardId"
              element={
                <PrivateRoute>
                  <ModernMoodBoardEditor />
                </PrivateRoute>
              }
            />
            <Route
              path="/moodboards/:boardId"
              element={
                <PrivateRoute>
                  <MoodBoardEditorV2Wrapper />
                </PrivateRoute>
              }
            />
            <Route
              path="/moodboards-v3/:boardId"
              element={
                <PrivateRoute>
                  <MoodBoardEditorV3Wrapper />
                </PrivateRoute>
              }
            />
            <Route
              path="/teams/:teamId/moodboards-v3/:boardId"
              element={
                <PrivateRoute>
                  <MoodBoardEditorV3Wrapper />
                </PrivateRoute>
              }
            />
            <Route path="/" element={<LandingPage />} />
            
            {/* Support Pages */}
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
            <Route path="/terms-of-service" element={<TermsOfServicePage />} />
            <Route path="/accessibility" element={<AccessibilityPolicyPage />} />
            <Route path="/help-support" element={<HelpSupportPage />} />
            <Route path="/status" element={<StatusPage />} />
            <Route path="/billing/success" element={<BillingSuccess />} />
            <Route path="/billing/cancel" element={<BillingCancel />} />
          </Routes>
        </GlobalPresenceProvider>
      </AuthProvider>
    </Router>
  );
};

export default App;
